import { DriverEtaShareInfo, EtaShareCodeState } from '../../services/api/domains/EtaShare';
import EtaSharePositionDriver from './ui/EtaSharePositionDriver/EtaSharePositionDriver';
import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { VehicleModel } from '../../logic/vehicles';
import { Logic } from '../../logic/logic';
import { message } from 'antd';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import EtaSharePromo from '../../components/Promo/Promo';
import { useWindowDimensions } from '../../hooks/useWindowDimensions';
import { DISPATCHER_URL, SMALL_DEVICE_WIDTH } from '../../utils/constants/constants';
import { CustomUserEvents, InitiatedFrom } from '../../logic/CustomUserEvents';
import EtaShareUpdateOn from '../../components/UpdateOn/UpdateOn';
import EtaShareInfo from '../../components/EtaShareInfo/EtaShareInfo';

interface Props {
    logic: Logic;
}

const DriverEtaShareModule = (props: Props) => {
    const params = useParams();
    const { t } = useTranslation();
    const { width } = useWindowDimensions();
    const intervalRef = useRef(0);

    const MESSAGE_KEY = 'rl-message-key';
    const REFRESH_INTERVAL = 1 * 60 * 1000; //1min

    const [driverEtaShareInfo, setDriverEtaShareInfo] = useState<DriverEtaShareInfo | null>();
    const [isShareEnabled, setIsShareEnabled] = useState(false);
    const [isPromoVisible, setIsPromoVisible] = useState(false);

    const messageStyle = {
        width: width > SMALL_DEVICE_WIDTH ? width / 2 : '100%'
    };

    useEffect(() => {
        message.config({
            duration: 0,
            maxCount: 1
        });

        if (params?.code) {
            _getEtaShareInfo(params?.code);

            intervalRef.current = window.setInterval(() => {
                if (params?.code) {
                    _getEtaShareInfo(params?.code);
                }
            }, REFRESH_INTERVAL);
        }

        _showPromo();

        return () => clearInterval(intervalRef.current);
    }, []);

    useEffect(() => {
        const shareEnabled = !!(
            driverEtaShareInfo &&
            driverEtaShareInfo.sharingState === EtaShareCodeState.Active &&
            moment(driverEtaShareInfo.sharingValidUntil) > moment()
        );

        setIsShareEnabled(shareEnabled);

        if (driverEtaShareInfo) {
            switch (driverEtaShareInfo?.sharingState) {
                case EtaShareCodeState.Completed:
                    message.success({
                        content: t('EtaShare.transportCompleted'),
                        key: MESSAGE_KEY,
                        style: messageStyle
                    });
                    _showPromo(true);
                    break;
                case EtaShareCodeState.Cancelled:
                    message.warning({
                        content: `${driverEtaShareInfo?.driverName} ${t('EtaShare.canceledSharing')}`,
                        key: MESSAGE_KEY,
                        style: messageStyle
                    });
                    _showPromo(true);
                    break;
                default:
                    break;
            }

            if (driverEtaShareInfo.position) {
                props.logic
                    .map()
                    .vehicle()
                    .update(driverEtaShareInfo.position as VehicleModel, _getAlignLeft(isPromoVisible));
            }
        } else {
            props.logic.map().vehicle().remove();
        }
    }, [driverEtaShareInfo]);

    const _onPromoVisibleChange = () => {
        message.destroy(MESSAGE_KEY);
        props.logic.map().vehicle().fitVehicle(_getAlignLeft(!isPromoVisible));
        setIsPromoVisible(!isPromoVisible);
    };

    const _onTryDispatcherButton = () => {
        CustomUserEvents.tryDispatcherButton(InitiatedFrom.TEASER_SHARE_BUTTON, driverEtaShareInfo);
        window.open(DISPATCHER_URL, '_blank');
    };

    const _getEtaShareInfo = async (code: string) => {
        try {
            const result = await props.logic.apiService().etaShare().getDriverEtaShareInfo(code);
            setDriverEtaShareInfo(result);
        } catch (error: any) {
            console.error(error);

            if (error?.response?.status === 410) {
                _showErrorMessage(t('EtaShare.expiredSharing'));
            }

            if (error?.response?.status === 404) {
                _showErrorMessage(t('EtaShare.doesNotExistSharing'));
            }
        }
    };

    const _showErrorMessage = (text: string) => {
        message.error({
            content: text,
            key: MESSAGE_KEY,
            style: messageStyle
        });

        _showPromo(true);
    };

    const _showPromo = (afterStopSharing = false) => {
        if (width > SMALL_DEVICE_WIDTH || afterStopSharing) {
            setIsPromoVisible(true);
        }

        if (afterStopSharing) {
            clearInterval(intervalRef.current);
        }
    };

    const _getAlignLeft = (promoVisible: boolean) => (promoVisible ? -width / 2 / 2 / 2 : 0);

    return (
        <>
            <EtaSharePromo
                visible={isPromoVisible}
                isShareEnabled={isShareEnabled}
                onPromoVisibleChange={_onPromoVisibleChange}
                onTryDispatcherButton={_onTryDispatcherButton}
            />
            {isShareEnabled && (
                <>
                    <EtaShareUpdateOn
                        updatedOn={driverEtaShareInfo?.position?.updatedOn}
                        isPromoVisible={isPromoVisible}
                    />

                    {driverEtaShareInfo?.locationSharingEnabled ? (
                        <EtaSharePositionDriver
                            address={driverEtaShareInfo?.route?.destination?.address}
                            driverName={driverEtaShareInfo?.driverName}
                            remainingDistance={driverEtaShareInfo?.route?.remainingDistance}
                            routeEta={driverEtaShareInfo?.route?.eta}
                            isPromoVisible={isPromoVisible}
                            onTryDispatcherButton={_onTryDispatcherButton}
                        />
                    ) : (
                        <EtaShareInfo
                            address={driverEtaShareInfo?.route?.destination?.address}
                            name={driverEtaShareInfo?.driverName}
                            remainingDistance={driverEtaShareInfo?.route?.remainingDistance}
                            routeEta={driverEtaShareInfo?.route?.eta}
                            isPromoVisible={isPromoVisible}
                            logoUrl={driverEtaShareInfo?.driverAvatarUrl}
                        />
                    )}
                </>
            )}
        </>
    );
};

export default DriverEtaShareModule;
